import { useState, useCallback } from 'react';
import { Menu } from '@headlessui/react';
import {
  TrashIcon,
  EllipsisVerticalIcon,
  PencilSquareIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiHelpers } from '../../services/api';
import { useAuthStore } from '../../store/authStore';

export default function TextBlockWidget({ textBlock, onEdit, onDelete, readOnly = false }) {
  const { hasPermission } = useAuthStore();
  const canEdit = !readOnly && hasPermission('edit_dashboards');

  const stopPropagation = (e) => e.stopPropagation();

  const handleMenuClick = (e, callback) => {
    e.stopPropagation();
    e.preventDefault();
    callback?.();
  };

  const handleDelete = useCallback(async () => {
    if (!window.confirm('Delete this text block?')) return;
    try {
      await apiHelpers.deleteTextBlock(textBlock._id);
      onDelete?.(textBlock._id);
      toast.success('Text block deleted');
    } catch {
      toast.error('Failed to delete text block');
    }
  }, [textBlock?._id, onDelete]);

  return (
    <div className="flex flex-col h-full">
      {/* Header / Drag Handle */}
      <div className="drag-handle flex items-center justify-between px-4 py-2 border-b border-surface-200 dark:border-surface-700 cursor-grab active:cursor-grabbing bg-surface-50/50 dark:bg-surface-800/50">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <DocumentTextIcon className="w-4 h-4 text-violet-500 flex-shrink-0" />
          <span className="text-sm font-medium text-surface-700 dark:text-surface-300 truncate">
            {textBlock?.title || 'Text Block'}
          </span>
        </div>

        {canEdit && (
          <div className="flex items-center gap-1 flex-shrink-0 ml-2">
            <Menu as="div" className="relative" onClick={stopPropagation} onTouchStart={stopPropagation} onTouchEnd={stopPropagation}>
              <Menu.Button
                className="p-1 rounded hover:bg-surface-200 dark:hover:bg-surface-700"
                onClick={stopPropagation}
                onMouseDown={stopPropagation}
                onTouchStart={stopPropagation}
                onTouchEnd={stopPropagation}
              >
                <EllipsisVerticalIcon className="w-4 h-4 text-surface-500" />
              </Menu.Button>
              <Menu.Items className="absolute right-0 z-50 mt-1 w-40 bg-white dark:bg-surface-800 rounded-lg shadow-lg border border-surface-200 dark:border-surface-700 py-1">
                <button
                  type="button"
                  onClick={(e) => handleMenuClick(e, () => onEdit?.(textBlock))}
                  onMouseDown={stopPropagation}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-surface-700 dark:text-surface-300 hover:bg-surface-100 dark:hover:bg-surface-700 text-left"
                >
                  <PencilSquareIcon className="w-4 h-4" />
                  Edit
                </button>
                <button
                  type="button"
                  onClick={(e) => handleMenuClick(e, handleDelete)}
                  onMouseDown={stopPropagation}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-left"
                >
                  <TrashIcon className="w-4 h-4" />
                  Delete
                </button>
              </Menu.Items>
            </Menu>
          </div>
        )}
      </div>

      {/* Body - rendered HTML content */}
      <div className="flex-1 overflow-auto px-4 py-3">
        {textBlock?.content ? (
          <div
            className="prose prose-sm dark:prose-invert max-w-none prose-headings:my-2 prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 text-sm"
            dangerouslySetInnerHTML={{ __html: textBlock.content }}
          />
        ) : (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-surface-400">
            <DocumentTextIcon className="w-8 h-8" />
            <span className="text-sm">No content yet</span>
          </div>
        )}
      </div>
    </div>
  );
}
